import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socket } from '@/socket';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';
import { invalidateAllQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { useAuth } from '@clerk/clerk-react';

/**
 * Join `event-${planEventId}` on the flows Socket.IO namespace and invalidate publication-state queries
 * when the server emits `publicationStateUpdated` (after batch publish).
 */
export function useEventPublicationSocketSync(eventId: string | undefined) {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const joinedForRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!eventId) return;

    const invalidate = () => {
      void invalidatePublicationStateQueries(queryClient);
    };

    const onPublicationStateUpdated = () => {
      invalidate();
    };

    const onQuotaBalanceUpdated = () => {
      void invalidateAllQuotaBalances(queryClient);
    };

    socket.on('publicationStateUpdated', onPublicationStateUpdated);
    socket.on('quota:balance-updated', onQuotaBalanceUpdated);

    const joinRoom = async () => {
      try {
        const token = await getToken();
        if (token && !socket.connected) {
          socket.io.opts.extraHeaders = { Authorization: `Bearer ${token}` };
          socket.connect();
        }
        if (!socket.connected) return;

        socket.emit(
          'eventPublicationJoinRoom',
          { eventId },
          (response: { ok?: boolean; planEventId?: string } | undefined) => {
            if (import.meta.env.DEV && response?.planEventId) {
              joinedForRef.current = response.planEventId;
            }
          },
        );
        joinedForRef.current = eventId;
      } catch {
        // non-fatal: HTTP GET still works
      }
    };

    void joinRoom();

    const onConnect = () => {
      void joinRoom();
    };
    socket.on('connect', onConnect);

    return () => {
      socket.off('publicationStateUpdated', onPublicationStateUpdated);
      socket.off('quota:balance-updated', onQuotaBalanceUpdated);
      socket.off('connect', onConnect);
    };
  }, [eventId, getToken, queryClient]);
}
